import React from "react";
import { trackOutboundLink } from "@/lib/analytics";

export interface PromoGame {
  name: string;
  url: string;
  faviconUrl?: string;
  backgroundColor?: string;
  imageClassName?: string;
}

interface GamePromoPanelProps {
  games?: PromoGame[];
  title?: string;
}

const DEFAULT_GAMES: PromoGame[] = [
  {
    name: "Causeway",
    url: "https://causeway.babblegram.app",
    backgroundColor: "linear-gradient(to top, #4a000d 0%, #000000 100%)",
  },
  {
    name: "LuckyPucker",
    url: "https://www.luckypucker.com",
    faviconUrl: "/luckypucker.png",
    imageClassName: "w-14 h-14",
    backgroundColor: "linear-gradient(135deg, #ffffff 0%, #e0f2fe 50%, #7dd3fc 100%)",
  },
  {
    name: "BabbleBattle",
    url: "https://battle.babblegram.app",
    backgroundColor: "#000000",
  },
];

function getFaviconUrl(game: PromoGame): string {
  if (game.faviconUrl) return game.faviconUrl;
  try {
    const origin = new URL(game.url).origin;
    return `${origin}/favicon.ico`;
  } catch {
    return "/favicon.ico";
  }
}

function getBackgroundColor(game: PromoGame, index: number): string {
  if (game.backgroundColor) return game.backgroundColor;
  const fallbackColors = ["#0f172a", "#1e3a5f", "#3f0e3e"];
  return fallbackColors[index % fallbackColors.length];
}

export const GamePromoPanel: React.FC<GamePromoPanelProps> = ({
  games = DEFAULT_GAMES,
  title = "More of our games",
}) => {
  return (
    <div
      className="mt-6 mb-4 rounded-2xl"
      style={{
        padding: "4px",
        background:
          "linear-gradient(135deg, #6b7280 0%, #374151 40%, #111827 100%)",
      }}
    >
      <div
        className="rounded-2xl p-4 dot-pattern"
        style={{
          background: "linear-gradient(to top, #1f2937 0%, #0b0f19 100%)",
        }}
      >
        <h3
          className="text-center text-sm font-semibold uppercase tracking-wide mb-4"
          style={{ color: "#de0046" }}
        >
          {title}
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {games.map((game, index) => {
            const favicon = getFaviconUrl(game);
            const bgColor = getBackgroundColor(game, index);
            return (
              <a
                key={game.url}
                href={game.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center text-center no-underline"
                aria-label={`Play ${game.name}`}
                onClick={() => trackOutboundLink(game.name, game.url, "more_games_panel")}
              >
                <div
                  className="p-[2px] rounded-2xl transition-transform duration-200 ease-out group-hover:scale-105 group-hover:-translate-y-1"
                  style={{
                    background:
                      "linear-gradient(135deg, #6b7280 0%, #374151 40%, #111827 100%)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  }}
                >
                  <div
                    className="w-[60px] h-[60px] rounded-[14px] flex items-center justify-center"
                    style={{
                      background: bgColor,
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.15)",
                    }}
                  >
                    <img
                      src={favicon}
                      alt=""
                      className={`${game.imageClassName ?? "w-10 h-10"} object-contain`}
                      style={{ aspectRatio: "1 / 1" }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = "none";
                      }}
                    />
                  </div>
                </div>
                <span className="mt-2 text-xs font-medium text-gray-200 leading-tight group-hover:text-white transition-colors">
                  {game.name}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GamePromoPanel;
